const PREFIX = 'dha_nexus_feedback_mutation:v1';
export const FEEDBACK_MUTATION_CHANGE_EVENT = 'dha_nexus_feedback_mutation_change';

function notifyMutationChange(actorUserId) {
  if (typeof window === 'undefined' || !actorUserId) return;
  window.dispatchEvent(
    new CustomEvent(FEEDBACK_MUTATION_CHANGE_EVENT, { detail: { actorUserId } }),
  );
}

function storage() {
  return typeof window === 'undefined' ? null : window.sessionStorage;
}

function normalize(value) {
  if (Array.isArray(value)) return value.map(normalize);
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((result, key) => {
        result[key] = normalize(value[key]);
        return result;
      }, {});
  }
  return typeof value === 'string' ? value.trim() : value ?? null;
}

export function feedbackMutationSignature(value) {
  return JSON.stringify(normalize(value));
}

export function feedbackMutationStorageKey({ actorUserId, operation, resourceId }) {
  if (!actorUserId || !operation || !resourceId) return null;
  return `${PREFIX}:${actorUserId}:${operation}:${resourceId}`;
}

function newKey() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  // Browser crypto is available in supported PRIME browsers. This fallback
  // retains UUID shape for older local test environments only.
  return '00000000-0000-4000-8000-'.concat(Math.random().toString(16).slice(2).padEnd(12, '0').slice(0, 12));
}

export function pendingFeedbackMutation({ actorUserId, operation, resourceId, signature, payload }) {
  const key = feedbackMutationStorageKey({ actorUserId, operation, resourceId });
  const target = storage();
  if (!key || !target) return { key: newKey(), persistent: false };
  try {
    const existing = JSON.parse(target.getItem(key) || 'null');
    if (existing?.key && existing?.signature === signature) {
      return { key: existing.key, persistent: true, payload: existing.payload };
    }
    // `payload` is optional: flows that must REPLAY the exact original request
    // after an ambiguous outcome (Round 3 UAT/onboarding fencing) store it so
    // a reload can resolve with the original body, never edited data.
    const pending =
      payload === undefined ? { key: newKey(), signature } : { key: newKey(), signature, payload };
    target.setItem(key, JSON.stringify(pending));
    notifyMutationChange(actorUserId);
    return { key: pending.key, persistent: true, payload: pending.payload };
  } catch {
    return { key: newKey(), persistent: false };
  }
}

export function getPendingFeedbackMutation({ actorUserId, operation, resourceId }) {
  const key = feedbackMutationStorageKey({ actorUserId, operation, resourceId });
  const target = storage();
  if (!key || !target) return null;
  try {
    const existing = JSON.parse(target.getItem(key) || 'null');
    return existing?.key ? existing : null;
  } catch {
    return null;
  }
}

export function clearPendingFeedbackMutation({ actorUserId, operation, resourceId }) {
  const key = feedbackMutationStorageKey({ actorUserId, operation, resourceId });
  if (!key) return;
  try {
    storage()?.removeItem(key);
    notifyMutationChange(actorUserId);
  } catch {
    /* browser storage is advisory; the server remains authoritative */
  }
}

export function pendingFeedbackMutationsForActor(actorUserId) {
  const target = storage();
  if (!target || !actorUserId) return {};
  const prefix = `${PREFIX}:${actorUserId}:`;
  const pending = {};
  try {
    for (let index = 0; index < target.length; index += 1) {
      const key = target.key(index);
      if (!key?.startsWith(prefix)) continue;
      const value = JSON.parse(target.getItem(key) || 'null');
      if (value?.key && value?.signature) pending[key.slice(prefix.length)] = value;
    }
  } catch {
    return {};
  }
  return pending;
}

export function restorePendingFeedbackMutations(actorUserId, pending = {}) {
  const target = storage();
  if (!target || !actorUserId || !pending || typeof pending !== 'object') return;
  const prefix = `${PREFIX}:${actorUserId}:`;
  try {
    for (const [identity, value] of Object.entries(pending)) {
      if (!identity || identity.includes(':') === false || !value?.key || !value?.signature) continue;
      const key = `${prefix}${identity}`;
      if (!target.getItem(key)) target.setItem(key, JSON.stringify(value));
    }
  } catch {
    /* browser storage is advisory; the server remains authoritative */
  }
}

export function shouldRetainFeedbackMutation(error) {
  return ![
    'VALIDATION_ERROR',
    'INVALID_IDEMPOTENCY_KEY',
    'IDEMPOTENCY_KEY_REUSED',
    'ACCESS_DENIED',
    'FORBIDDEN',
    'FEEDBACK_ALREADY_CONVERTED',
    'FEEDBACK_PACKAGE_ALREADY_SUBMITTED',
    'FEEDBACK_ALREADY_NEEDS_INFO',
    'INVALID_STATUS_TRANSITION',
    'REVISION_CONFLICT',
    'NOT_FOUND',
  ].includes(String(error?.code || ''));
}
